// frontend/src/screens/admin/sps/ImportPayDataScreen.jsx
import { useState, useRef } from 'react';
import { Container, Row, Col, Card, Button, Alert, Spinner, Table, Badge } from 'react-bootstrap';
import { useGetPayDataSummaryQuery } from '../../../slices/spsApiSlices';
import { useBatchImport }            from '../../../hooks/useBatchImport';
import ImportProgress                from '../../../components/sps/ImportProgress';
import BasketballLoader              from '../../../components/sps/BasketballLoader';

const PAY_COLUMN_MAP = [
  { file: 'PPSNumber',                          stored: 'ppsNumber',                       note: 'Key'      },
  { file: 'Employee Name',                       stored: 'employeeName',                    note: ''         },
  { file: 'Payment Frequency',                   stored: 'paymentFrequency',                note: 'Required' },
  { file: 'Pay Period',                          stored: 'payPeriod',                       note: 'Required' },
  { file: 'Gross Basic Pensionable Pay',         stored: 'grossBasicPensionablePay',        note: 'Negative ok'},
  { file: 'Gross Basic Pensionable Allowances',  stored: 'grossBasicPensionableAllowances', note: 'Negative ok'},
  { file: 'FTE at Pay Date',                     stored: 'fteAtPayDate',                    note: '0–1'      },
  { file: 'PenshionRefund',                      stored: 'pensionRefund',                   note: 'Optional' },
];

function DropZone({ onFile, disabled }) {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const style = {
    border: `2px dashed ${dragOver ? '#0d6efd' : '#ced4da'}`,
    borderRadius: 8, padding: '3rem 2rem', textAlign: 'center',
    background: dragOver ? '#f0f5ff' : '#fafafa',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all .2s', opacity: disabled ? 0.6 : 1,
  };
  return (
    <div style={style}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) onFile(f); }}
      onClick={() => !disabled && inputRef.current?.click()}
    >
      <div style={{ fontSize: '2.5rem' }}>📂</div>
      <div className="fw-semibold mt-2">Drag &amp; drop your XLSM file here, or click to browse</div>
      <div className="text-muted small mt-1">Accepts .xlsm · .xlsx — the Data sheet will be imported</div>
      <input ref={inputRef} type="file" accept=".xlsm,.xlsx,.xls" style={{ display: 'none' }}
        onChange={(e) => onFile(e.target.files[0])} disabled={disabled} />
    </div>
  );
}

export default function ImportPayDataScreen() {
  const { data: summary, refetch: refetchSummary } = useGetPayDataSummaryQuery();

  const { startImport, state, reset } = useBatchImport({
    endpoint:  '/api/pay-records/import-batch',
    sheetName: 'Data',
    batchSize: 200,  // 200 rows × ~257 bytes = ~50kb per POST, well under any limit
  });

  const [selectedFile, setSelectedFile] = useState(null);

  const isRunning = state.status === 'reading' || state.status === 'importing';
  const isDone    = state.status === 'done';

  const handleFile = (file) => { setSelectedFile(file); reset(); };

  const handleImport = async () => {
    if (!selectedFile) return;
    await startImport(selectedFile);
    refetchSummary();
  };

  const fmtNum = (n) => Number(n || 0).toLocaleString();

  return (
    <Container fluid className="py-3">
      <Row className="mb-3">
        <Col>
          <h4 className="mb-0">Import Pay Data</h4>
          <small className="text-muted">
            File is read in the browser and sent in batches — exact progress shown row by row
          </small>
        </Col>
      </Row>
      <Row className="g-3">
        <Col lg={7} className="d-flex flex-column gap-3">
          <Card>
            <Card.Header className="fw-semibold bg-light">Upload File</Card.Header>
            <Card.Body>

              {/* Show basketball animation while running, drop zone otherwise */}
              {isRunning
                ? <BasketballLoader isRunning={isRunning} />
                : <DropZone onFile={handleFile} disabled={false} />
              }

              {/* Selected file info + import button */}
              {selectedFile && !isRunning && !isDone && (
                <Alert variant="info" className="mt-3 mb-0 d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div>
                    <strong>{selectedFile.name}</strong>
                    <span className="ms-2 text-muted">({(selectedFile.size / 1024 / 1024).toFixed(1)} MB)</span>
                  </div>
                  <Button variant="primary" onClick={handleImport}>
                    ⬆ Import Now
                  </Button>
                </Alert>
              )}

              {/* Real progress bar */}
              <ImportProgress state={state} />

              {isDone && (
                <div className="mt-3">
                  <Button variant="outline-secondary" size="sm"
                    onClick={() => { setSelectedFile(null); reset(); }}>
                    Import another file
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>

          <Card>
            <Card.Header className="fw-semibold bg-light">How it works</Card.Header>
            <Card.Body className="small text-muted">
              <ul className="mb-0 ps-3">
                <li>The file is read in your browser using SheetJS — nothing leaves your machine until you click Import.</li>
                <li>Rows are sent to DB in batches of 500. The progress bar updates after each batch completes.</li>
                <li>Records are <strong>upserted</strong> on (PPS + pay period) — safe to re-import.</li>
                <li>After import, run <strong>Sync from Pay Data</strong> to update the Staff Directory.</li>
              </ul>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header className="fw-semibold bg-light">
              Expected Column Names
              <span className="text-muted fw-normal ms-2" style={{ fontSize: '.78rem' }}>(from the Data sheet)</span>
            </Card.Header>
            <Card.Body className="p-0">
              <Table size="sm" className="mb-0" style={{ fontSize: '.78rem' }}>
                <thead className="table-dark">
                  <tr><th>Column in Excel</th><th>Stored as</th><th style={{ width: 80 }}>Notes</th></tr>
                </thead>
                <tbody>
                  {PAY_COLUMN_MAP.map((col) => (
                    <tr key={col.file}>
                      <td className="font-monospace" style={{ fontSize: '.73rem' }}>{col.file}</td>
                      <td className="font-monospace text-primary" style={{ fontSize: '.73rem' }}>{col.stored}</td>
                      <td>
                        {col.note === 'Required'     && <Badge bg="danger"              style={{ fontSize: '.6rem' }}>Required</Badge>}
                        {col.note === 'Key'          && <Badge bg="danger"              style={{ fontSize: '.6rem' }}>Key</Badge>}
                        {col.note === 'Negative ok'  && <Badge bg="warning" text="dark" style={{ fontSize: '.6rem' }}>±</Badge>}
                        {col.note === 'Optional'     && <Badge bg="secondary"           style={{ fontSize: '.6rem' }}>Optional</Badge>}
                        {col.note === '0–1'          && <Badge bg="info"    text="dark" style={{ fontSize: '.6rem' }}>0–1</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={5} className="d-flex flex-column gap-3">
          {/* Current data summary */}
          <Card>
            <Card.Header className="fw-semibold bg-light d-flex justify-content-between">
              <span>Current Data in DB Pay File</span>
              {summary && <Button size="sm" variant="link" className="p-0" onClick={refetchSummary}>Refresh</Button>}
            </Card.Header>
            <Card.Body>
              {!summary ? <div className="text-center py-3"><Spinner animation="border" /></div> : (
                <>
                  <Row className="g-2 mb-3">
                    {[
                      ['Total Records', fmtNum(summary.totalRecords), 'primary'],
                      ['Staff Members', fmtNum(summary.totalStaff),   'success'],
                      ['Year range', summary.minYear && summary.maxYear ? `${summary.minYear}–${summary.maxYear}` : '—', 'secondary'],
                    ].map(([label, val, col]) => (
                      <Col xs={4} key={label}>
                        <Card className={`border-${col} text-center`}>
                          <Card.Body className="p-2">
                            <div className={`fw-bold text-${col}`} style={{ fontSize: '1.1rem' }}>{val}</div>
                            <div className="text-muted" style={{ fontSize: '.72rem' }}>{label}</div>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                  <div className="fw-semibold small mb-2">Records by year</div>
                  <div style={{ maxHeight: 290, overflowY: 'auto' }}>
                    <Table size="sm" bordered className="mb-0">
                      <thead className="table-dark" style={{ position: 'sticky', top: 0 }}>
                        <tr><th>Year</th><th>Freq</th><th className="text-end">Records</th><th className="text-end">Staff</th></tr>
                      </thead>
                      <tbody>
                        {(summary.byYear || []).map((row, i) => (
                          <tr key={i}>
                            <td className="fw-semibold">{row.year}</td>
                            <td><Badge bg={row.freq === 'Monthly' ? 'primary' : 'success'} style={{ fontSize: '.7rem' }}>{row.freq}</Badge></td>
                            <td className="text-end">{fmtNum(row.records)}</td>
                            <td className="text-end">{fmtNum(row.staff)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>

          {/* Data format rules */}
          <Card>
            <Card.Header className="fw-semibold bg-light">Data Format Rules</Card.Header>
            <Card.Body className="p-0">
              <Table size="sm" className="mb-0" style={{ fontSize: '.78rem' }}>
                <thead className="table-dark">
                  <tr><th>Field</th><th>Valid values</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="font-monospace" style={{ fontSize: '.73rem', whiteSpace: 'nowrap' }}>Payment Frequency</td>
                    <td>
                      <Badge bg="primary" className="me-1" style={{ fontSize: '.65rem' }}>Monthly</Badge>
                      <Badge bg="success" style={{ fontSize: '.65rem' }}>Fortnightly</Badge>
                    </td>
                  </tr>
                  <tr>
                    <td className="font-monospace" style={{ fontSize: '.73rem', whiteSpace: 'nowrap' }}>Pay Period — Monthly</td>
                    <td>
                      <span style={{ fontFamily: 'monospace', fontSize: '.72rem' }}>
                        YYYY01 – YYYY12
                      </span>
                      <div className="text-muted" style={{ fontSize: '.68rem' }}>
                        e.g. 201301 → 201312, 202501 → 202512
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="font-monospace" style={{ fontSize: '.73rem', whiteSpace: 'nowrap' }}>Pay Period — Fortnightly (2013 - 2024)</td>
                    <td>
                      <span style={{ fontFamily: 'monospace', fontSize: '.72rem' }}>
                        YYYY02 – YYYY54
                      </span>
                      <div className="text-muted" style={{ fontSize: '.68rem' }}>
                        e.g. 201302 → 201304 →  201306 →.... 201354
                      </div>
                    </td>
                  </tr>

                  <tr>
                    <td className="font-monospace" style={{ fontSize: '.73rem', whiteSpace: 'nowrap' }}>Pay Period — Fortnightly (2025 → YYYYY)</td>
                    <td>
                      <span style={{ fontFamily: 'monospace', fontSize: '.72rem' }}>
                        YYYY01 – YYYY26
                      </span>
                      <div className="text-muted" style={{ fontSize: '.68rem' }}>
                        e.g. 202501 → 202502 → 202503 →.... 202526
                      </div>
                    </td>
                  </tr>

                  <tr>
                    <td className="font-monospace" style={{ fontSize: '.73rem', whiteSpace: 'nowrap' }}>FTE at Pay Date</td>
                    <td>
                      <span style={{ fontFamily: 'monospace', fontSize: '.72rem' }}>0.001 – 1.000</span>
                      <div className="text-muted" style={{ fontSize: '.68rem' }}>
                        Decimal fraction of full-time — 0.5 = half time
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="font-monospace" style={{ fontSize: '.73rem', whiteSpace: 'nowrap' }}>Gross Basic Pensionable Pay</td>
                    <td>
                      <span style={{ fontFamily: 'monospace', fontSize: '.72rem' }}>Numeric — negative ok</span>
                      <div className="text-muted" style={{ fontSize: '.68rem' }}>
                        Negative = clawback / correction record
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="font-monospace" style={{ fontSize: '.73rem', whiteSpace: 'nowrap' }}>PenshionRefund</td>
                    <td>
                      <Badge bg="success" className="me-1" style={{ fontSize: '.65rem' }}>1 = Yes</Badge>
                      <Badge bg="secondary" style={{ fontSize: '.65rem' }}>0 = No</Badge>
                      <div className="text-muted" style={{ fontSize: '.68rem' }}>
                        Pension refund indicator — defaults to 0 if absent
                      </div>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
